import { useState } from 'react'
import { UploadCloud, FileSpreadsheet, AlertTriangle, Loader2, ClipboardPaste } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Progress } from '@/components/ui/progress'
import EmptyState from '@/components/common/EmptyState'
import useDropzone from '@/hooks/useDropzone'

const kb = (n) => (n > 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`)

/**
 * LA BANDEJA — suelta aquí TODO lo que el cliente te dio.
 *
 * El asistente de antes pedía un archivo y una sección, de uno en uno. El cliente no
 * entrega así: entrega un USB con "MIS DATOS.xlsx", tres CSV sueltos y un archivo que no se
 * puede leer. Aquí entra todo de una vez y el sistema dice qué es cada hoja.
 *
 * Lo ilegible NO desaparece: se queda listado con su motivo, para que el asesor pueda
 * enseñárselo al cliente y pedirle el archivo bueno sin tener que reproducir el error.
 */
export default function Bandeja({ proyecto, subiendo, progresoSubida, onArchivos, onClasificar, clasificando }) {
  const { arrastrando, propsZona, propsInput } = useDropzone(onArchivos, {
    disabled: subiendo,
    multiple: true,
    accept: '.xlsx,.xlsm,.csv,.txt,.tsv,.json,.zip',
  })

  const [pegado, setPegado] = useState('')
  const [pegando, setPegando] = useState(false)

  const archivos = proyecto?.archivos || []
  const sinReconocer = proyecto?.totales?.sinReconocer || 0

  // «El cliente me mandó cuatro filas por WhatsApp» era el único caso que justificaba el
  // asistente rápido aparte. No hace falta otro asistente: el texto se envuelve en un archivo
  // aquí mismo y entra por la misma puerta que todo lo demás.
  const usarPegado = async () => {
    if (!pegado.trim()) return
    setPegando(true)
    try {
      const nombre = `pegado-${new Date().toISOString().slice(0, 10)}.csv`
      await onArchivos([new File([pegado], nombre, { type: 'text/csv' })])
      setPegado('')
    } finally {
      setPegando(false)
    }
  }

  return (
    <div className="space-y-4">
      <div
        {...propsZona}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && propsZona.onClick?.()}
        className={cn(
          'flex flex-col items-center justify-center rounded-lg border-2 border-dashed px-6 py-10 text-center transition-colors',
          arrastrando ? 'border-primary bg-primary-50 dark:bg-primary-900/40' : 'border-neutral-300 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800/60 hover:border-primary-400 dark:hover:border-primary-600',
          subiendo ? 'cursor-default opacity-70' : 'cursor-pointer'
        )}
      >
        <input {...propsInput} />
        {subiendo ? (
          <Loader2 size={28} className="mb-2 animate-spin text-primary" />
        ) : (
          <UploadCloud size={28} className="mb-2 text-neutral-400 dark:text-neutral-500" />
        )}
        <p className="text-body font-medium text-neutral-900 dark:text-neutral-50">
          {subiendo ? 'Subiendo…' : 'Suelta aquí todo lo que te dio el cliente'}
        </p>
        <p className="mt-0.5 text-caption text-neutral-500 dark:text-neutral-400">
          Excel, CSV, JSON — y el ZIP con las fotos del catálogo. Varios a la vez.
        </p>
        {subiendo && progresoSubida != null && <Progress value={progresoSubida} className="mt-3 max-w-xs" />}
      </div>

      <details className="rounded-lg border border-neutral-200 dark:border-neutral-800 px-3 py-2">
        <summary className="flex cursor-pointer select-none items-center gap-1.5 text-body-sm font-medium text-neutral-700 dark:text-neutral-300">
          <ClipboardPaste size={15} /> …o pega las filas, si te las mandaron por mensaje
        </summary>
        <div className="mt-3 space-y-2">
          <Textarea
            rows={5}
            placeholder={'codigo,descripcion,precio\nP-001,Martillo,12.50'}
            value={pegado}
            onChange={(e) => setPegado(e.target.value)}
          />
          <p className="text-caption text-neutral-500 dark:text-neutral-400">
            La primera línea son los nombres de las columnas. Sin ella, la primera fila se toma como
            encabezado y ese dato no se migra.
          </p>
          <Button variant="secondary" size="sm" disabled={!pegado.trim() || pegando || subiendo} onClick={usarPegado}>
            {pegando ? 'Subiendo…' : 'Usar lo pegado'}
          </Button>
        </div>
      </details>

      {archivos.length === 0 ? (
        <EmptyState
          icono={<FileSpreadsheet size={28} />}
          titulo="Todavía no has traído nada"
          descripcion="No hace falta que esté ordenado ni que venga una sección por archivo: de eso se encarga el siguiente paso."
        />
      ) : (
        <>
          <ul className="space-y-2">
            {archivos.map((a) => (
              <li key={a.archivoId}>
                <Card>
                  <CardContent className="flex flex-wrap items-center gap-x-3 gap-y-1 py-3">
                    <FileSpreadsheet size={16} className={a.rechazo ? 'text-danger-700 dark:text-danger-300' : 'text-neutral-400 dark:text-neutral-500'} />
                    <span className="font-medium text-neutral-900 dark:text-neutral-50">{a.nombre}</span>
                    <span className="text-caption tabular-nums text-neutral-500 dark:text-neutral-400">{kb(a.bytes)}</span>
                    {a.rechazo ? (
                      <Badge variant="danger">No se pudo leer</Badge>
                    ) : (
                      <Badge variant="secondary">
                        {a.hojas} hoja{a.hojas === 1 ? '' : 's'}
                      </Badge>
                    )}
                    {/* El motivo, completo y en el idioma del asesor: es lo que le va a
                        reenviar al cliente para pedirle el archivo bueno. */}
                    {a.rechazo && <p className="w-full text-body-sm text-danger-700 dark:text-danger-300">{a.rechazo}</p>}
                    {(a.avisos || []).map((av) => (
                      <p key={av} className="flex w-full items-start gap-1.5 text-body-sm text-warning-700 dark:text-warning-300">
                        <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                        {av}
                      </p>
                    ))}
                  </CardContent>
                </Card>
              </li>
            ))}
          </ul>

          <div className="flex flex-wrap items-center gap-2">
            <Button onClick={onClasificar} disabled={clasificando}>
              {clasificando ? 'Reconociendo…' : 'Reconocer qué es cada hoja'}
            </Button>
            {sinReconocer > 0 && (
              <span className="text-caption text-neutral-500 dark:text-neutral-400">
                {sinReconocer} hoja(s) sin reconocer — las asignas tú en el siguiente paso.
              </span>
            )}
          </div>
        </>
      )}
    </div>
  )
}
